import React, { useEffect, useMemo, useState } from 'react';
import { Users, MagnifyingGlass, PencilSimple, Check, X, ShieldCheck, CreditCard, Buildings, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { platformAPI } from '../../utils/api';

/**
 * Admin-only users panel surfaced on the Platform Settings page.
 *
 * Scope (intentionally narrow):
 *   - Search users by name/email.
 *   - Promote between RIDER and ORG_ADMIN.
 *   - Link to an existing organization.
 *   - Grant / revoke active PRODUCER subscription.
 *
 * What's deliberately NOT here:
 *   - Password resets (go through /forgot-password)
 *   - Email changes (auth flow)
 *   - User deletion (needs org cascade; reserved for future)
 */
export const AdminUsersPanel = () => {
  const { user } = useAuth();
  const isMasterOnly = (user?.roles || [user?.role]).includes('MASTER');
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [lmasterCount, setLmasterCount] = useState({ count: 0, max: 2 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);    // user object currently open in the edit panel
  const [saving, setSaving] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(null);  // org pending confirmation

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: userList }, { data: orgList }] = await Promise.all([
        platformAPI.listUsers(q),
        platformAPI.listOrgs(),
      ]);
      setUsers(userList || []);
      setOrgs(orgList || []);
      // LMASTER count is best-effort; not blocking load.
      try {
        const { data } = await platformAPI.lmasterCount();
        setLmasterCount(data || { count: 0, max: 2 });
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await platformAPI.listUsers(q);
      setUsers(data || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (u) => setEditing({ ...u, _draftRole: u.role, _draftOrg: u.organization_id || '', _draftName: u.name, _grantSub: u.has_producer_subscription });

  const closeEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const payload = {
      role: editing._draftRole,
      name: editing._draftName,
      organization_id: editing._draftOrg || null,
    };
    // Only pass grant/revoke when it differs from the stored state.
    if (editing._grantSub !== editing.has_producer_subscription) {
      payload.grant_producer_subscription = !!editing._grantSub;
    }
    setSaving(true);
    try {
      await platformAPI.updateUser(editing.id, payload);
      toast.success(`${editing.name || editing.email} updated`);
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111] border border-[#1F1F1F] rounded-2xl p-6 sm:p-8 mt-6" data-testid="admin-users-panel">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-full bg-[#FF9500]/15 flex items-center justify-center flex-shrink-0">
          <Users size={22} weight="duotone" className="text-[#FF9500]" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Users
          </h2>
          <p className="text-sm text-[#9E9E9E] mt-1 max-w-lg">
            Promote accounts to ORG_ADMIN, link them to an organization, and grant producer subscriptions.
            Password changes still flow through the user's login page.
          </p>
        </div>
      </div>

      <form onSubmit={runSearch} className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
            data-testid="admin-users-search"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-[#FF9500] hover:bg-[#FF9500]/85 text-black text-sm font-bold rounded-md"
          data-testid="admin-users-search-btn"
        >
          Search
        </button>
      </form>

      {loading ? (
        <div className="py-8 text-center text-[#6B6B6B] text-sm">Loading...</div>
      ) : users.length === 0 ? (
        <div className="py-8 text-center text-[#6B6B6B] text-sm">No users found. Try a different search.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]">
                <th className="py-2 pr-3">Name / Email</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Organization</th>
                <th className="py-2 pr-3">Producer</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1F1F1F] hover:bg-[#161616]/50" data-testid={`admin-user-row-${u.id}`}>
                  <td className="py-3 pr-3">
                    <div className="text-white font-semibold">{u.name || '(no name)'}</div>
                    <div className="text-[11px] text-[#6B6B6B]">{u.email}</div>
                  </td>
                  <td className="py-3 pr-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      u.role === 'MASTER' ? 'bg-[#FACC15]/15 text-[#FACC15]'
                      : u.role === 'LMASTER' ? 'bg-[#FF9500]/15 text-[#FF9500]'
                      : u.role === 'ORG_ADMIN' ? 'bg-[#0096FF]/15 text-[#0096FF]'
                      : 'bg-[#262626] text-[#9E9E9E]'
                    }`}>{u.role}</span>
                  </td>
                  <td className="py-3 pr-3 text-[#9E9E9E] text-[12px]">
                    {u.organization_name || <span className="text-[#555] italic">—</span>}
                  </td>
                  <td className="py-3 pr-3">
                    {u.has_producer_subscription
                      ? <span className="text-[#34C759] flex items-center gap-1 text-[11px] font-semibold"><ShieldCheck size={12} weight="bold" /> Active</span>
                      : <span className="text-[#555] text-[11px]">None</span>}
                  </td>
                  <td className="py-3 pr-3 text-right">
                    <button
                      onClick={() => openEdit(u)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500] text-white rounded text-[11px] font-semibold transition-colors"
                      data-testid={`admin-user-edit-${u.id}`}
                    >
                      <PencilSimple size={12} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !saving && closeEdit()} data-testid="admin-user-edit-modal">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Edit User</h3>
              <button onClick={closeEdit} className="text-[#9E9E9E] hover:text-white" data-testid="admin-user-close-modal">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] font-semibold mb-1">Email</label>
                <div className="text-white text-sm bg-[#0A0A0A] border border-[#1F1F1F] rounded px-3 py-2">{editing.email}</div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] font-semibold mb-1">Name</label>
                <input
                  type="text"
                  value={editing._draftName || ''}
                  onChange={(e) => setEditing({ ...editing, _draftName: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF9500]"
                  data-testid="admin-user-name-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] font-semibold mb-1">Role</label>
                <select
                  value={editing._draftRole}
                  onChange={(e) => setEditing({ ...editing, _draftRole: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF9500]"
                  data-testid="admin-user-role-select"
                >
                  <option value="RIDER">RIDER</option>
                  <option value="ORG_ADMIN">ORG_ADMIN (Producer / Organization Owner)</option>
                  {isMasterOnly && (
                    <option
                      value="LMASTER"
                      disabled={lmasterCount.count >= lmasterCount.max && editing.role !== 'LMASTER'}
                    >
                      LMASTER (Lesser Admin) — {lmasterCount.count}/{lmasterCount.max} used
                    </option>
                  )}
                  {['MASTER'].includes(editing.role) && (
                    <option value={editing.role} disabled>{editing.role} (cannot change — protected)</option>
                  )}
                </select>
                {isMasterOnly && (
                  <p className="text-[10px] text-[#6B6B6B] mt-1">
                    LMASTER admins have all MASTER access except changing platform settings &amp; deleting organizations.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] font-semibold mb-1">Organization</label>
                <select
                  value={editing._draftOrg || ''}
                  onChange={(e) => setEditing({ ...editing, _draftOrg: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF9500]"
                  data-testid="admin-user-org-select"
                >
                  <option value="">(none)</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-[#6B6B6B] mt-1">For ORG_ADMINs, link to the organization they own.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="admin-user-sub-toggle-label">
                <input
                  type="checkbox"
                  checked={!!editing._grantSub}
                  onChange={(e) => setEditing({ ...editing, _grantSub: e.target.checked })}
                  className="w-4 h-4 accent-[#34C759]"
                  data-testid="admin-user-sub-toggle"
                />
                <span className="flex items-center gap-1.5 text-sm text-white">
                  <CreditCard size={14} weight="bold" className="text-[#34C759]" />
                  Active Producer Subscription
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-[#262626]">
              <button
                onClick={closeEdit}
                disabled={saving}
                className="px-4 py-2 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white"
                data-testid="admin-user-cancel-btn"
              >Cancel</button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF9500] hover:bg-[#FF9500]/85 text-black rounded-md text-xs font-bold disabled:opacity-60"
                data-testid="admin-user-save-btn"
              >
                <Check size={14} weight="bold" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Organizations management — MASTER only. Lists all orgs with their show
          and member counts and exposes a destructive Delete with confirmation. */}
      {isMasterOnly && (
        <div className="mt-8 pt-6 border-t border-[#1F1F1F]" data-testid="admin-orgs-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
              <Buildings size={18} weight="duotone" className="text-[#FF3B30]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>Organizations</h3>
              <p className="text-xs text-[#9E9E9E]">Delete an organization to permanently remove it and cascade-delete all of its shows.</p>
            </div>
          </div>
          {orgs.length === 0 ? (
            <p className="text-[#6B6B6B] text-sm">No organizations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-orgs-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3 w-20 text-center">Shows</th>
                    <th className="py-2 pr-3 w-20 text-center">Members</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => (
                    <tr key={o.id} className="border-b border-[#1F1F1F] hover:bg-[#161616]/50" data-testid={`admin-org-row-${o.id}`}>
                      <td className="py-2.5 pr-3 text-white font-semibold">{o.name}</td>
                      <td className="py-2.5 pr-3 text-center text-[#9E9E9E]">{o.show_count}</td>
                      <td className="py-2.5 pr-3 text-center text-[#9E9E9E]">{o.member_count}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <button
                          onClick={() => setDeletingOrg(o)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20 text-[#FF3B30] rounded text-[11px] font-bold transition-colors"
                          data-testid={`admin-org-delete-${o.id}`}
                        >
                          <Trash size={11} weight="bold" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete-org confirmation modal */}
      {deletingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !saving && setDeletingOrg(null)} data-testid="admin-org-delete-modal">
          <div className="bg-[#161616] border border-[#FF3B30]/40 rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#FF3B30]/15 flex items-center justify-center">
                <Trash size={18} weight="bold" className="text-[#FF3B30]" />
              </div>
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Delete Organization</h3>
            </div>
            <p className="text-sm text-[#9E9E9E] mb-3">
              You are about to permanently delete <span className="text-white font-semibold">{deletingOrg.name}</span>. This will:
            </p>
            <ul className="list-disc list-inside text-sm text-[#9E9E9E] space-y-1 mb-4">
              <li>Delete <span className="text-[#FF3B30] font-bold">{deletingOrg.show_count} show{deletingOrg.show_count !== 1 ? 's' : ''}</span> (with all entries, scores, payouts, and reservations)</li>
              <li>Detach <span className="text-white font-bold">{deletingOrg.member_count} member{deletingOrg.member_count !== 1 ? 's' : ''}</span> from this organization (member accounts are preserved)</li>
              <li>Cancel any organization subscriptions</li>
            </ul>
            <p className="text-xs text-[#FF3B30] mb-5 font-semibold">This action cannot be undone.</p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeletingOrg(null)}
                disabled={saving}
                className="px-4 py-2 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white"
                data-testid="admin-org-delete-cancel"
              >Cancel</button>
              <button
                onClick={async () => {
                  setSaving(true);
                  try {
                    await platformAPI.deleteOrganization(deletingOrg.id);
                    toast.success(`${deletingOrg.name} deleted`);
                    setDeletingOrg(null);
                    await loadAll();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Failed to delete organization');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="px-4 py-2 bg-[#FF3B30] hover:bg-[#FF3B30]/85 text-white rounded-md text-xs font-bold disabled:opacity-60"
                data-testid="admin-org-delete-confirm"
              >
                {saving ? 'Deleting…' : `Yes, Delete ${deletingOrg.name}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPanel;
